import Tool, { type ToolArgs } from '../../../tools/Tool';
import React, { useState, useRef } from 'react';
import { Transforms, Node, Editor, Element } from 'slate';
import { ReactEditor } from 'slate-react';
import styles from './EditLink.module.css';
import toolbarStyles from '../../../components/Toolbar/Toolbar.module.css';
import classNames from 'classnames';
import { isLinkElement } from '../../../types/guards';
import useFocusElementToolbarInput from '../../../components/ElementToolbar/useFocusElementToolbarInput';
import { displayURL } from '../utils';
import type { Placement } from '@floating-ui/dom';

const EditLinkModal: React.FC<ToolArgs> = ({
  editor,
  element,
  toolbarHideModal,
}) => {
  const defaultValue =
    element && isLinkElement(element) ? element.link_href ?? '' : '';
  const [originalValue] = useState(defaultValue);
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  useFocusElementToolbarInput(inputRef);

  const defaultTitle =
    element && isLinkElement(element) ? Node.string(element) : '';
  const [originalTitle] = useState(defaultTitle);
  const [title, setTitle] = useState(defaultTitle);

  if (!element) return null;

  const path = ReactEditor.findPath(editor, element);

  const updateLinkElement = (href: string) => {
    Transforms.setNodes(
      editor,
      { link_href: href },
      {
        at: path,
      }
    );
  };

  const updateTextElement = (title: string) => {
    // If the title is empty, use the trimmed URL as the title.
    title ||= displayURL(value || originalValue, {
      protocol: true,
      www: true,
    });

    const nodeEntry = Editor.node(editor, path);
    if (!nodeEntry) return;
    const [node] = nodeEntry;
    if (!Element.isElement(node)) return;

    // Update text in the first child node
    Transforms.insertText(editor, title, { at: [...path, 0], voids: true });

    // Then remove all nodes after the first one
    for (let i = node.children.length - 1; i > 0; i--) {
      Transforms.removeNodes(editor, { at: [...path, i] });
    }
  };

  const handleUrlKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkElement(value);
        ReactEditor.focus(editor);
        break;
      case 'Escape':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkElement(originalValue);
        ReactEditor.focus(editor);
        break;
    }
  };

  const handleTitleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        toolbarHideModal?.();
        event.preventDefault();
        updateTextElement(title);
        ReactEditor.focus(editor);
        break;
      case 'Escape':
        toolbarHideModal?.();
        event.preventDefault();
        updateTextElement(originalTitle);
        ReactEditor.focus(editor);
        break;
    }
  };

  const handleUrlBlur = () => {
    if (value !== originalValue) {
      updateLinkElement(value);
    }
  };

  const handleTitleBlur = () => {
    if (title !== originalTitle) updateTextElement(title);
  };

  return (
    <div className={styles.editLink}>
      <label className={styles.label}>
        <div className={styles.labelName}>URL</div>
        <input
          ref={inputRef}
          type="text"
          className={classNames(
            'unstyled-input',
            toolbarStyles.toolbarInput,
            styles.input
          )}
          placeholder="URL"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleUrlKeyDown}
          onBlur={handleUrlBlur}
        />
      </label>

      <label className={styles.label}>
        <div className={styles.labelName}>Text</div>

        <input
          ref={textInputRef}
          type="text"
          className={classNames(
            'unstyled-input',
            toolbarStyles.toolbarInput,
            styles.input
          )}
          placeholder="Text"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={handleTitleKeyDown}
          onBlur={handleTitleBlur}
        />
      </label>
    </div>
  );
};

// Edit the link and title.
class ToolEditLink extends Tool {
  static toolName = 'Edit';
  static iconClass = 'fa-regular fa-link';
  static withoutRefocus = true;

  // Show the modal view to editor the URL.
  static action({ editor, element, toolbarShowModal }: ToolArgs): void {
    toolbarShowModal?.(ToolEditLink);
  }

  static renderToolbarButtonIcon({ editor, element }: ToolArgs): JSX.Element {
    return <span>Edit</span>;
  }

  static renderToolbarModal(toolArgs: ToolArgs): JSX.Element {
    return <EditLinkModal {...toolArgs} />;
  }

  // Open the edit link modal when the toolbar opens if the link is empty.
  static openToolbarModal({ editor, element }: ToolArgs): boolean {
    return (
      !element ||
      !isLinkElement(element) ||
      !element.link_href ||
      element.link_href.length === 0
    );
  }

  static handleModalClosed({ editor, element }: ToolArgs) {}

  static tooltipPlacement(): Placement {
    return 'bottom';
  }
}

export default ToolEditLink;
